import { useCallback, useEffect, useState } from "react";
import api, { euro } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Reports() {
  const [period, setPeriod] = useState("mese");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const today = new Date();
    let from = iso(today);
    if (period === "settimana") { const s = new Date(today); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); from = iso(s); }
    if (period === "mese") from = iso(new Date(today.getFullYear(), today.getMonth(), 1));
    if (period === "anno") from = iso(new Date(today.getFullYear(), 0, 1));
    const { data } = await api.get("/reports", { params: { date_from: from, date_to: iso(today) } });
    setData(data);
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const Box = ({ title, children, tid }) => (
    <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={tid}>
      <h3 className="font-heading font-bold mb-3">{title}</h3>
      {children}
    </div>
  );

  const Row = ({ l, v }) => (
    <div className="flex justify-between py-1.5 text-sm border-b border-slate-50 last:border-0">
      <span className="text-slate-600">{l}</span><span className="font-semibold">{v}</span>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-center gap-2">
        <h1 className="font-heading text-2xl font-bold">Report</h1>
        <div className="ml-auto flex bg-white rounded-full border border-slate-200 p-1" data-testid="reports-period">
          {[["settimana", "Settimana"], ["mese", "Mese"], ["anno", "Anno"]].map(([v, l]) => (
            <button key={v} data-testid={`reports-period-${v}`} onClick={() => setPeriod(v)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium ${period === v ? "bg-blue-600 text-white" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>

      {data && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          <Box title="Pazienti" tid="report-patients">
            <Row l="Nuovi pazienti" v={data.patients.new} />
            <Row l="Pazienti attivi" v={data.patients.active} />
            <Row l="Sedute completate" v={data.patients.sessions} />
          </Box>
          <Box title="Appuntamenti" tid="report-appointments">
            <Row l="Totali" v={data.appointments.total} />
            <Row l="Effettuati" v={data.appointments.done} />
            <Row l="Annullati" v={data.appointments.cancelled} />
            <Row l="Non presentati" v={data.appointments.no_show} />
          </Box>
          <Box title="Economico" tid="report-economics">
            <Row l="Entrate" v={euro(data.economics.entrate)} />
            <Row l="Uscite" v={euro(data.economics.uscite)} />
            <Row l="Saldo" v={euro(data.economics.saldo)} />
          </Box>
          <Box title="Palestra" tid="report-gym">
            <Row l="Ingressi" v={data.gym.entries} />
            <Row l="Incassi" v={euro(data.gym.revenue)} />
          </Box>
          <Box title="Fisioterapisti" tid="report-physios">
            {data.physios.length === 0 ? <p className="text-sm text-slate-400">Nessun dato</p> : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.physios} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="appointments" name="Appuntamenti" fill="#2563EB" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            {data.physios.map((p) => <Row key={p.name} l={p.name} v={`${p.appointments} app. · ${p.hours}h`} />)}
          </Box>
          <Box title="Utilizzo macchinari" tid="report-machines">
            {data.machines.length === 0 ? <p className="text-sm text-slate-400">Nessun utilizzo registrato</p> :
              data.machines.map((m) => <Row key={m.name} l={m.name} v={`${m.uses} utilizzi`} />)}
          </Box>
        </div>
      )}
    </div>
  );
}
